import Divider from '@mui/material/Divider';
import Menu, { type MenuProps } from '@mui/material/Menu';
import { usePermissions } from '@tyro/api';
import {
  getMenuItemList,
  type MenuItemConfig,
  SubMenu,
} from './menu-item-list';
import { MENU_TRANSITION_DURATION, MenuTransition } from './menu-transition';
import {
  SubmenuIntentProvider,
  useSubmenuIntent,
} from './use-submenu-hover-intent';

export interface ContextMenuProps {
  id?: string;
  buttonRef?: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  menuProps?: Partial<MenuProps>;
  menuItems: MenuItemConfig[] | MenuItemConfig[][];
  anchorReference?: MenuProps['anchorReference'];
  anchorPosition?: MenuProps['anchorPosition'];
}

export function ContextMenu(props: ContextMenuProps) {
  return (
    <SubmenuIntentProvider open={props.isOpen}>
      <ContextMenuInner {...props} />
    </SubmenuIntentProvider>
  );
}

function ContextMenuInner({
  id,
  buttonRef,
  isOpen,
  onClose,
  menuProps,
  menuItems,
  anchorReference = 'anchorEl',
  anchorPosition,
}: ContextMenuProps) {
  const permissions = usePermissions();
  const intent = useSubmenuIntent();
  const isCursorAnchored = anchorReference === 'anchorPosition';
  const activeSubmenu = intent?.activeSubmenu ?? null;
  const sectionedMenuItems = (
    Array.isArray(menuItems[0]) ? menuItems : [menuItems]
  ) as MenuItemConfig[][];

  return (
    <>
      <Menu
        id={id}
        open={isOpen}
        anchorReference={anchorReference}
        anchorEl={isCursorAnchored ? undefined : (buttonRef?.current ?? null)}
        anchorPosition={anchorPosition}
        disableEnforceFocus
        transitionDuration={MENU_TRANSITION_DURATION}
        slots={{ transition: MenuTransition }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: isCursorAnchored ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        {...menuProps}
        onClose={(...args) => {
          if (args[1] === 'backdropClick') {
            const event = args[0] as React.MouseEvent;
            event.stopPropagation();
            event.preventDefault();
          }
          onClose();
          menuProps?.onClose?.(...args);
        }}
        sx={{
          ml: isCursorAnchored ? 0 : 1,
          '& .MuiMenu-list': {
            py: 0.75,
          },
          '& hr': {
            my: 1,
          },
          ...menuProps?.sx,
        }}
      >
        {sectionedMenuItems.reduce<React.ReactNode[]>((acc, section, index) => {
          const sectionItems = getMenuItemList({
            menuItems: section,
            permissions,
            onClose,
          });

          if (acc.length > 0 && sectionItems.length > 0) {
            acc.push(<Divider key={`divider-${index}`} />);
          }

          if (sectionItems.length > 0) {
            acc.push(...sectionItems);
          }

          return acc;
        }, [])}
      </Menu>
      {activeSubmenu && (
        <SubMenu
          submenuId={activeSubmenu.id}
          anchorEl={activeSubmenu.anchorEl}
          open
          items={activeSubmenu.items as MenuItemConfig[]}
          permissions={permissions}
          onClose={activeSubmenu.onClose}
        />
      )}
    </>
  );
}
